<template>
    <div>
        <api-errors :errors="getErrors" />
        <loading v-if="loading" />
        <div v-else-if="ready">
            <div class="row margin-bottom-15">
                <div class="col-md-12">
                    <div class="filter-container pull-left">
                        <div class="input-group">
                            <div class="input-group">
                                <div class="input-group-btn" style="width: 40%;">
                                    <select class="form-control" v-model="filterColumn"
                                        aria-label="Filter field selector" style="border-radius: 4px 0 0 4px;">
                                        <option value="">Select field to filter</option>
                                        <option v-for="field in filterableFields" :key="field" :value="field">
                                            {{ formatLabel(field) }}
                                        </option>
                                    </select>
                                </div>
                                <input type="text" class="form-control" v-model="filterValue"
                                    placeholder="Enter filter value..." @keyup.enter="handleEnterKey" />
                                <div class="input-group-btn">
                                    <button class="btn btn-primary" @click="applyFilter"
                                        :disabled="isFilterButtonDisabled">
                                        Filter
                                    </button>
                                    <button class="btn btn-default" @click="clearFilter"
                                        :disabled="!filterColumnQuery && !filterValueQuery">
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="btn-group pull-right">
                        <button class="btn btn-primary" disabled>
                            <i class="glyphicon glyphicon-th-large"></i> Card View
                        </button>
                        <a :href="listViewUrl" class="btn btn-default">
                            <i class="glyphicon glyphicon-th-list"></i> List View
                        </a>
                    </div>
                </div>
            </div>
            <div class="panel-body">
                <div class="row" style="display: flex; min-height: calc(100vh - 100px);">
                    <div class="col-md-3" style="padding: 15px;">
                        <div class="panel panel-default" style="height: 100%;">
                            <div class="panel-heading">
                                <template v-if="refinementField && refinementField.length > 0">
                                    {{ formatLabel(refinementField[0]) }}
                                </template>
                                <template v-else>
                                    No refinement field available
                                </template>
                            </div>
                            <div class="panel-body">
                                <ul class="list-group" v-if="filteredRefinementValues.length > 0">
                                    <li v-for="value in filteredRefinementValues" :key="value"
                                        class="list-group-item refinement-item"
                                        :class="{ 'active': selectedRefinement === (typeof value === 'object' ? Object.values(value)[0] : value) }"
                                        @click="handleRefinementClick(typeof value === 'object' ? Object.values(value)[0] : value)"
                                        style="cursor: pointer;">
                                        {{ typeof value === 'object' ? Object.values(value)[0] : value }}
                                    </li>
                                </ul>
                                <p v-else>No refinement values available</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-9" style="padding: 15px;">
                        <div class="row cards-grid">
                            <div v-for="card in transformedCards" :key="card.id" class="col-sm-6 col-md-4">
                                <div class="panel panel-default inventory-card">
                                    <div v-if="card.id && card.id !== 'Unknown ID'" class="panel-heading id-header">
                                        <a :href="card.absolute_url" class="id-link" target="_blank">
                                            <span class="truncate-text" :title="card.id">{{ card.id }}</span>
                                        </a>
                                    </div>

                                    <div v-if="card.name && card.name !== 'Unnamed'" class="panel-heading name-header">
                                        <span class="truncate-text" :title="card.name">
                                            {{ card.name }}
                                        </span>
                                    </div>
                                    <div v-if="card.description" class="panel-body">
                                        <p class="description" :class="{ expanded: isExpanded(card.id) }"
                                            @click="toggleTruncation(card.id)"
                                            :title="!isExpanded(card.id) ? card.description : ''">
                                            {{ card.description }}
                                        </p>
                                    </div>
                                    <div v-if="hasVisibleCustomFields(card.custom_fields)" class="panel-body custom-fields">
                                        <dl>
                                            <template v-for="(value, key) in card.custom_fields">
                                                <div v-if="isFieldVisible(key, value, card)" :key="key" class="field-row">
                                                    <dt>{{ formatLabel(key) }}:</dt>
                                                    <dd>
                                                        <div class="truncate-container">
                                                            <span class="truncate-text" :title="value">
                                                                {{ formatDateValue(key, value) }}
                                                            </span>
                                                        </div>
                                                    </dd>
                                                </div>
                                            </template>
                                        </dl>
                                    </div>

                                    <div class="panel-footer">
                                        <a :href="card.absolute_url" class="details-link" target="_blank">Details</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="text-center">
                <ve-pagination :layout="['total', 'prev', 'pager', 'next', 'jumper']" :total="totalCount"
                    :page-index="pageIndex" :page-size="pageSize" @on-page-number-change="pageNumberChange" />
            </div>
        </div>
    </div>
</template>

<script>
import Loading from 'src/components/Loading.vue'
import apiRequest from 'src/mixins/api-request.js'
import apiErrors from '@/ApiErrorDisplay.vue'
import { VePagination } from 'vue-easytable'
import { reverse } from 'src/lib/data/urls'

export default {
    name: 'InventoryCard',
    components: {
        'loading': Loading,
        'api-errors': apiErrors,
        've-pagination': VePagination
    },
    mixins: [apiRequest],
    props: {
        inventoryUuid: {
            type: String,
            required: true
        },
        stewardshipSlug: {
            type: String,
            required: false
        },
        inStewardshipContext: {
            type: Boolean,
            default: false
        },
        displayNameHelpMapping: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            ready: false,
            loading: true,
            expandedCards: new Set(),
            cardData: null,
            getErrors: {},
            totalCount: 0,
            pageIndex: 1,
            pageSize: 20,
            inventoryItemsEndpoint: '',
            filterColumn: '',
            filterValue: '',
            filterColumnQuery: '',
            filterValueQuery: '',
            selectedRefinement: null,
            refinementFieldValue: [],
            refinementValues: [],
            jsonParseDisplayNameHelpMapping: {},
            fieldDisplayNameHelpMap: new Map(),

        }
    },
    computed: {
        listViewUrl() {
            if (this.inStewardshipContext && this.stewardshipSlug) {
                return reverse('stewardship_inventory_list', [this.stewardshipSlug, this.inventoryUuid])
            } else {
                return reverse('inventory_list', [this.inventoryUuid])
            }
        },
        filteredRefinementValues() {
            return this.refinementValues.filter(value =>
                value !== null && value !== undefined && value !== ''
            );
        },
        isDateField() {
            const dateFields = ['date', 'modified', 'created']
            return dateFields.some(field => this.filterColumn?.toLowerCase().includes(field))
        },
        isFilterButtonDisabled() {
            if (!this.filterColumn || !this.filterValue) return true
            return false
        },
        filterableFields() {
            if (!this.cardData?.card_columns) return []
            const fields = [
                ...(this.cardData.card_columns.id || []),
                ...(this.cardData.card_columns.name || []),
                ...(this.cardData.card_columns.description || []),
                ...(this.cardData.card_columns.custom_fields || [])
            ]

            return [...new Set(fields)]
        },
        refinementField() {
            if (!this.cardData?.card_columns) return []
            const fields = [
                ...(this.cardData.card_columns.refinement || [])
            ]
            return [...new Set(fields)]
        },
        transformedCards() {
            try {
                if (!this.cardData?.inventory_items || !this.cardData?.card_columns) {
                    return [];
                }

                return this.cardData.inventory_items.map(item => {
                    try {
                        const firstIdColumn = this.cardData.card_columns.id?.[0];
                        const id = this.extractValues(item, [firstIdColumn]);

                        const firstNameColumn = this.cardData.card_columns.name?.[0] || 'name';
                        const name = this.extractValues(item, [firstNameColumn]);

                        const firstDescColumn = this.cardData.card_columns.description?.[0];
                        const description = this.extractValues(item, firstDescColumn ? [firstDescColumn] : []);

                        const customFields = this.extractCustomFields(item);

                        return {
                            id: id || 'Unknown ID',
                            name: name || 'Unnamed',
                            description: description || '',
                            custom_fields: customFields || {},
                            absolute_url: item.absolute_url || '#',
                            refinement: this.extractValues(item, this.cardData.card_columns.refinement || [])
                        };
                    } catch (itemError) {
                        console.error('Error processing inventory item:', itemError);
                        return {
                            id: 'Error',
                            name: 'Error processing item',
                            description: '',
                            custom_fields: {},
                            absolute_url: '#',
                            refinement: null
                        };
                    }
                });
            } catch (error) {
                console.error('Error in transformedCards:', error);
                return [];
            }
        }
    },
    watch: {
        'cardData.card_columns': {
            handler(newColumns) {
                if (newColumns?.refinement) {
                    this.refinementFieldValue = [...newColumns.refinement]
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.inventoryItemsEndpoint = `/inventory/api/${this.inventoryUuid}/items/`
        this.getTableData()
        this.jsonParseDisplayNameHelpMapping = JSON.parse(this.displayNameHelpMapping)
        this.fieldDisplayNameHelpMap = this.getNameToDisplayNameHelpMap()
    },
    methods: {
         hasVisibleCustomFields(customFields) {
            for (const [key, value] of Object.entries(customFields)) {
                if (this.isFieldVisible(key, value)) {
                    return true;
                }
            }
            return false;
        },
        isFieldVisible(key, value) {
            return key !== this.refinementFieldValue[0] &&
                value !== null &&
                value !== undefined &&
                value !== '' &&
                value !== 'No data available';
        },
        formatDateValue(key, value) {
            if (!value) return 'No data available';
            if (key === 'created' ||
                key === 'modified' ||
                key.toLowerCase().includes('date')) {
                try {
                    const date = new Date(value);
                    return new Intl.DateTimeFormat(navigator.language).format(date).replace(/\//g, '-');
                } catch (e) {
                    return value;
                }
            }
            return value;
        },
        getNameToDisplayNameHelpMap () {
            let map = new Map()
            let allFields = [...this.jsonParseDisplayNameHelpMapping.fields, ...this.jsonParseDisplayNameHelpMapping.custom_fields, ...this.jsonParseDisplayNameHelpMapping.alternative_name_types, ...this.jsonParseDisplayNameHelpMapping.namespaces]
            allFields.forEach(obj => {
                map.set(obj.value, {displayName: obj.name, helpText: obj.help_text})
            })
            return map
        },
        populateRefinementValues() {
            try {
                if (this.refinementFieldValue.length === 0 && this.cardData?.card_columns?.refinement?.length > 0) {
                    this.refinementFieldValue = [...this.cardData.card_columns.refinement];
                }

                if (!this.cardData?.inventory_items || !this.refinementFieldValue.length) {
                    this.refinementValues = [];
                    return;
                }

                const field = this.refinementFieldValue[0];

                try {
                    const values = this.cardData.inventory_items
                        .map(item => {
                            try {
                                return this.extractValues(item, [field]);
                            } catch (itemError) {
                                console.error(`Error extracting refinement value for item:`, itemError);
                                return null;
                            }
                        });

                    this.refinementValues = [...new Set(values)];

                    if (this.filteredRefinementValues.length === 0) {
                        this.selectedRefinement = null;
                    }
                } catch (valuesError) {
                    console.error('Error processing refinement values:', valuesError);
                    this.refinementValues = [];
                }
            } catch (error) {
                console.error('Error in populateRefinementValues:', error);
                this.refinementValues = [];
            }
        },
        handleRefinementClick(value) {
            const refinementValue = typeof value === 'object' ? Object.values(value)[0] : value;
            this.filterValue = refinementValue
            this.filterColumn = this.refinementFieldValue[0]
            this.pageIndex = 1
            this.applyFilter()
        },
        handleEnterKey() {
            if (!this.isFilterButtonDisabled) {
                this.applyFilter()
            }
        },
        applyFilter() {
            if (this.filterColumn && this.filterValue) {
                this.filterColumnQuery = this.filterColumn
                this.filterValueQuery = this.filterValue
                this.pageIndex = 1
                this.updateEndpointAndFetch()
            }
        },
        clearFilter() {
            this.filterColumn = ''
            this.filterValue = ''
            this.filterColumnQuery = ''
            this.filterValueQuery = ''
            this.pageIndex = 1
            this.updateEndpointAndFetch()
        },
        updateEndpointAndFetch() {
            let endpoint = `/inventory/api/${this.inventoryUuid}/items/?page=${this.pageIndex}`

            if (this.filterColumnQuery && this.filterValueQuery) {
                endpoint += `&filter=${encodeURIComponent(this.filterColumnQuery)}&value=${encodeURIComponent(this.filterValueQuery)}`
            }

            this.inventoryItemsEndpoint = endpoint
            this.getTableData()
        },
        pageNumberChange(pageIndex) {
            this.pageIndex = pageIndex
            this.updateEndpointAndFetch()
        },
       getTableData() {
            try {
                this.loading = true;
                const tableDataPromise = this.get(this.inventoryItemsEndpoint);

                tableDataPromise.then(response => {
                    try {
                        if (!response || !response.data) {
                            throw new Error('Invalid response format');
                        }

                        this.cardData = response.data.results;
                        this.totalCount = response.data.count || 0;
                        this.populateRefinementValues();
                    } catch (dataError) {
                        console.error('Error processing response data:', dataError);
                        this.getErrors = { general: 'Error processing response data' };
                    } finally {
                        this.ready = true;
                        this.loading = false;
                    }
                }).catch(error => {
                    console.error('API request error:', error);
                    this.getErrors = error.response?.data || { general: 'Failed to fetch data' };
                    this.ready = true;
                    this.loading = false;
                });
            } catch (error) {
                console.error('Error initiating data fetch:', error);
                this.getErrors = { general: 'Failed to initiate data fetch' };
                this.ready = true;
                this.loading = false;
            }
        },
      extractFieldValue(item, field) {
    try {
        if (!item || !field) return null;

        // Special handling for current_statuses
        if (field === 'current_statuses') {
            try {
                if (item.current_statuses?.length > 0) {
                    return item.current_statuses.map(status =>
                        `${status.registration_authority_name}:${status.state_name}`
                    ).join(', ');
                } else {
                    return 'No data available';
                }
            } catch (innerError) {
                console.error('Error processing current_statuses:', innerError);
                return 'Error processing statuses';
            }
        }

        // Check custom values
        if (item.custom_values?.length > 0) {
            try {
                const customValue = item.custom_values.find(custom_value =>
                    custom_value.system_name === field || custom_value.field_name === field
                );
                if (customValue?.content) {
                    return this.stripHtmlTags(customValue.content);
                }
            } catch (innerError) {
                console.error(`Error processing custom values for field ${field}:`, innerError);
            }
        }

        // Check identifiers/namespaces
        if (item.identifiers?.length > 0) {
            try {
                const identifier = item.identifiers.find(id => id.namespace_name === field);
                if (identifier) {
                    return `${identifier.identifier}${identifier.version ? '/' + identifier.version : ''}`;
                }
            } catch (innerError) {
                console.error(`Error processing identifiers for field ${field}:`, innerError);
            }
        }

        // Check alternative names
        if (item.alternative_names?.length > 0) {
            try {
                const altName = item.alternative_names.find(an => an.alternative_name_type_title === field);
                if (altName?.value) {
                    return this.stripHtmlTags(altName.value);
                }
            } catch (innerError) {
                console.error(`Error processing alternative names for field ${field}:`, innerError);
            }
        }

        // Check direct property access
        try {
            const value = item[field];
            if (value !== undefined && value !== null) {
                return this.stripHtmlTags(value);
            }
        } catch (innerError) {
            console.error(`Error accessing direct property ${field}:`, innerError);
        }

        return null;
    } catch (error) {
        console.error(`Error extracting field ${field}:`, error);
        return null;
    }
},

extractValues(item, fields) {
    try {
        if (!item || !fields || !Array.isArray(fields)) return null;

        for (const field of fields) {
            const value = this.extractFieldValue(item, field);
            if (value !== null) {
                return value;
            }
        }
        return null;
    } catch (error) {
        console.error('Error in extractValues:', error);
        return null;
    }
},

extractCustomFields(item) {
    try {
        const customFields = {};

        if (!this.cardData?.card_columns?.custom_fields?.length) {
            return customFields;
        }

        this.cardData.card_columns.custom_fields.forEach(field => {
            try {
                const value = this.extractFieldValue(item, field);
                customFields[field] = value;
            } catch (fieldError) {
                console.error(`Error processing custom field ${field}:`, fieldError);
                customFields[field] = null;
            }
        });

        return customFields;
    } catch (error) {
        console.error('Error in extractCustomFields:', error);
        return {};
    }
},
        stripHtmlTags(str) {
            try {
                if (typeof str !== 'string') return str;
                return str.replace(/<[^>]*>/g, '');
            } catch (error) {
                console.error('Error stripping HTML tags:', error);
                return str || '';
            }
        },
        isExpanded(cardId) {
            return this.expandedCards.has(cardId)
        },
        toggleTruncation(cardId) {
            if (this.expandedCards.has(cardId)) {
                this.expandedCards.delete(cardId)
            } else {
                this.expandedCards.add(cardId)
            }
        },
        formatLabel(key) {
            if (this.fieldDisplayNameHelpMap.has(key)) {
                return this.fieldDisplayNameHelpMap.get(key).displayName
            }
            const specialCases = {
                'uuid': 'UUID',
                'id': 'ID'
            }

            if (specialCases[key.toLowerCase()]) {
                return specialCases[key.toLowerCase()]
            }

            return key
                .split(/[\s_]+/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
        }
    }
}
</script>
<style scoped>
.cards-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px;
}

.cards-grid>[class*='col-'] {
    padding: 0 10px;
    margin-bottom: 20px;
    display: flex;
}

.inventory-card {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0;
    background-color: #f8f9fa;
    border: 1px solid #ddd;
}

.id-header {
    background-color: #e8f1f8;
    color: #31708f;
    padding: 8px 15px;
    border-bottom: 1px solid #ddd;
}

.name-header {
    background-color: #f5f5f5;
    padding: 8px 15px;
    border-bottom: 1px solid #ddd;
}

.panel-body {
    padding: 15px;
    background-color: white;
}

.custom-fields {
    background-color: white;
    border-top: 1px solid #ddd;
    padding-top: 15px;
}

dl {
    margin: 0;
}

.field-row {
    margin-bottom: 8px;
    display: flex;
    flex-wrap: wrap;
}

.text-overflow {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
}

.field-row {
    margin-bottom: 8px;
    display: flex;
}

.truncate-text {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.truncate-container {
    width: 100%;
    position: relative;
}

.description {
    margin: 0;
    color: #666;
    font-size: 13px;
    overflow: hidden;
    position: relative;
    line-height: 1.4;
    max-height: 4.2em;
    cursor: pointer;
}

.description:before {
    content: '...';
    position: absolute;
    right: 0;
    bottom: 0;
    background: white;
    padding-left: 3px;
}

.description:after {
    content: '';
    position: absolute;
    right: 0;
    width: 1em;
    height: 1em;
    margin-top: 0.2em;
    background: white;
}

.description.expanded {
    max-height: none;
    padding-right: 0;
}

.description.expanded:before,
.description.expanded:after {
    display: none;
}

.field-row {
    margin-bottom: 8px;
    display: flex;
    align-items: flex-start;
    width: 100%;
}

dt {
    color: #666;
    font-weight: normal;
    margin-right: 5px;
    flex: 0 0 auto;
}

dd {
    flex: 1;
    min-width: 0;
    margin-left: 0;
}

@media (max-width: 767px) {
    .filter-container {
        margin-top: 15px;
    }

    .field-row {
        flex-direction: column;
    }

    dt {
        margin-bottom: 2px;
    }
}

.panel-heading {
    overflow: hidden;
}

.id-header,
.name-header {
    position: relative;
}

.panel-body {
    padding: 15px;
    word-wrap: break-word;
}

.custom-fields dl {
    margin-bottom: 0;
}

.panel-footer {
    background-color: #f5f5f5;
    padding: 8px 15px;
    text-align: left;
    margin-top: auto;
}

.details-link {
    color: #337ab7;
    text-decoration: none;
}

.details-link:hover {
    text-decoration: underline;
}

@media (max-width: 767px) {
    .filter-container {
        margin-top: 15px;
    }
}

.input-group-btn select.form-control {
    border-right: 0;
    width: 100%;
}
</style>
